// Reactive store for provider-spawned subagents (Codex / OpenCode / Claude).
//
// Reimplements Paseo's zustand `useProviderSubagentStore` (packages/app/src/
// subagents/provider-store.ts) as a Svelte 5 runes class. It holds the descriptor
// set the daemon streams via `provider_subagent` update events and drives the
// subagents track. The LIVE CONVERSATION is not duplicated here: each timeline
// row is forwarded into the shared AgentStreamState keyed by the subagent id, so
// the existing AgentStreamView renders a provider subagent's real message stream
// with zero new timeline plumbing.
//
// The stream target is injected (ProviderSubagentStreamSink) so this class stays
// unit-testable without importing the heavy app-state module — the app wires the
// real sink (agentStreamState) at construction in app.svelte.ts.

import type { AgentEvent, AgentTimelineItem } from "./types.js";
import {
  isProviderSubagentFinished,
  providerSubagentKey,
  sortProviderSubagentRows,
  toProviderSubagentRow,
  type ProviderSubagentDescriptor,
  type ProviderSubagentRow,
  type ProviderSubagentUpdate,
} from "./provider-subagents.js";

// The slice of AgentStreamState this store drives. Narrow on purpose so a test
// can pass a spy and assert exactly which timeline/terminal events were fed.
export interface ProviderSubagentStreamSink {
  handleEvent(agentId: string, event: AgentEvent): void;
  clear(agentId: string): void;
  // Swap the rows for this seq-ordered list; pending permission cards are untouched.
  replace(agentId: string, items: Record<string, unknown>[]): void;
}

// One transcript row as both the live update and the fetched page carry it.
export interface SubagentTranscriptRow {
  seq: number;
  item: unknown;
}

// How long a "running" subagent may go without ANY new data (descriptor upsert,
// timeline row, or list snapshot) before the UI stops presenting it as live.
// Running subagents produce no terminal event and never emit turn_started, so
// the stream store's idle watchdog never arms for them — when a daemon dies
// mid-fanout the inspector otherwise says "Waiting for the subagent's first
// messages…" forever. Past it a row is "stalled": out of the working counts, archivable.
// Ceiling naming mirrors WAKE_CEILING_MS (state/cybo-wake.svelte.ts).
export const SUBAGENT_STALL_CEILING_MS = 4 * 60 * 1000;

// Synthesize the terminal turn event that mirrors a finished descriptor, so the
// reused AgentStreamView shows the turn boundary / error instead of hanging on
// "running". Running subagents produce no terminal event.
function terminalEventFor(desc: ProviderSubagentDescriptor): AgentEvent | null {
  if (desc.status === "running") return null;
  if (desc.status === "failed") {
    return { type: "turn_failed", provider: desc.provider, error: "Subagent failed" };
  }
  if (desc.status === "canceled") {
    return { type: "turn_canceled", provider: desc.provider, reason: "canceled" };
  }
  return { type: "turn_completed", provider: desc.provider };
}

export class ProviderSubagentStore {
  private static readonly RETAINED_WORKSPACES = 3;
  // Keyed by providerSubagentKey(parentAgentId, subagentId).
  descriptors = $state<Map<string, ProviderSubagentDescriptor>>(new Map());
  // Finished subagents the user archived off the track (Paseo's hiddenFromTrack).
  // A subagent that goes back to running clears its hidden flag.
  hiddenFromTrack = $state<Set<string>>(new Set());
  // When THIS client last received any data about a subagent (descriptor upsert,
  // timeline row, or list snapshot), keyed by subagent id. The signal behind the
  // stalled state: only an arriving update can bump it, so it can't report
  // liveness that didn't happen. Reactive so the panels re-derive on updates.
  lastUpdateAt = $state<Map<string, number>>(new Map());
  // Subagents whose fetched transcript was CUT — the server said rows exist behind
  // the page it served (one tail page, no back-paging; and the relay's durable mirror
  // is retention-bounded). Held here rather than derived from the entries because
  // nothing in the stream itself records what was NOT sent. The drill-down renders it
  // so a partial transcript never passes as the whole thing.
  truncatedTranscripts = $state<Set<string>>(new Set());
  // Bumped when a running subagent crosses the stall ceiling, so counts re-derive with no update.
  stallEpoch = $state(0);
  private stallTimer: ReturnType<typeof setTimeout> | undefined;

  // Subagents a route is showing right now: a list snapshot that omits one (an archived
  // child is absent from it) must neither drop it nor clear its transcript.
  private readonly viewing = new Set<string>();

  setViewing(subagentId: string, on: boolean): void {
    if (on) this.viewing.add(subagentId);
    else this.viewing.delete(subagentId);
  }

  private activeWorkspaceId: string | null = null;
  private workspaceClock = 0;
  private readonly workspaceByParent = new Map<string, string>();
  private readonly parentsByWorkspace = new Map<string, Set<string>>();
  private readonly workspaceRecency = new Map<string, number>();

  private readonly sink: ProviderSubagentStreamSink;
  // Rows drawn per subagent, by daemon seq: live and fetched rows are the same rows.
  // ponytail: one daemon generation; a restart reuses seqs until the stream is cleared.
  private readonly drawn = new Map<string, Map<number, unknown>>();

  constructor(sink: ProviderSubagentStreamSink) {
    this.sink = sink;
  }

  bindParentWorkspace(parentAgentId: string, workspaceId: string): void {
    const previous = this.workspaceByParent.get(parentAgentId);
    if (previous && previous !== workspaceId) {
      const parents = this.parentsByWorkspace.get(previous);
      parents?.delete(parentAgentId);
      if (parents?.size === 0) this.parentsByWorkspace.delete(previous);
    }
    this.workspaceByParent.set(parentAgentId, workspaceId);
    const parents = this.parentsByWorkspace.get(workspaceId) ?? new Set<string>();
    parents.add(parentAgentId);
    this.parentsByWorkspace.set(workspaceId, parents);
    this.touchWorkspace(workspaceId);
    this.evictInactiveWorkspaces();
  }

  activateWorkspace(workspaceId: string): void {
    this.activeWorkspaceId = workspaceId;
    this.touchWorkspace(workspaceId);
    this.evictInactiveWorkspaces();
  }

  private touchWorkspace(workspaceId: string): void {
    this.workspaceRecency.set(workspaceId, ++this.workspaceClock);
  }

  private workspaceHasRunningSubagent(workspaceId: string): boolean {
    const parents = this.parentsByWorkspace.get(workspaceId);
    if (!parents) return false;
    for (const desc of this.descriptors.values()) {
      if (parents.has(desc.parentAgentId) && desc.status === "running") return true;
    }
    return false;
  }

  private evictInactiveWorkspaces(): void {
    while (this.workspaceRecency.size > ProviderSubagentStore.RETAINED_WORKSPACES) {
      const candidate = [...this.workspaceRecency.entries()]
        .filter(
          ([workspaceId]) =>
            workspaceId !== this.activeWorkspaceId &&
            !this.workspaceHasRunningSubagent(workspaceId),
        )
        .sort((a, b) => a[1] - b[1])[0]?.[0];
      if (!candidate) return;
      const parents = this.parentsByWorkspace.get(candidate) ?? new Set<string>();
      for (const parentAgentId of parents) this.removeParent(parentAgentId);
      this.parentsByWorkspace.delete(candidate);
      this.workspaceRecency.delete(candidate);
    }
  }

  private removeParent(parentAgentId: string): void {
    for (const desc of this.descriptors.values()) {
      if (desc.parentAgentId === parentAgentId) this.remove(parentAgentId, desc.id);
    }
    this.workspaceByParent.delete(parentAgentId);
  }

  // Apply one wire update. The single entry point the `provider_subagent` event
  // handler calls.
  applyUpdate(update: ProviderSubagentUpdate): void {
    if (update.kind === "upsert") {
      this.upsert(update.subagent);
      return;
    }
    if (update.kind === "remove") {
      this.remove(update.parentAgentId, update.subagentId);
      return;
    }
    // timeline: feed the row into the shared stream store under the subagent id.
    this.noteUpdate(update.subagentId);
    if (update.seq > 0) {
      const drawn = this.drawnFor(update.subagentId);
      if (drawn.has(update.seq)) return;
      drawn.set(update.seq, update.item);
    }
    this.sink.handleEvent(update.subagentId, {
      type: "timeline",
      provider: update.provider,
      item: update.item,
    });
  }

  private drawnFor(subagentId: string): Map<number, unknown> {
    let drawn = this.drawn.get(subagentId);
    if (!drawn) {
      drawn = new Map();
      this.drawn.set(subagentId, drawn);
    }
    return drawn;
  }

  private clearStream(subagentId: string): void {
    this.drawn.delete(subagentId);
    this.sink.clear(subagentId);
  }

  // Merge a fetched backlog under the live rows: newer rows append, older ones redraw all
  // in seq order (completion re-added last). Drawn seqs are skipped.
  mergeTranscript(
    parentAgentId: string,
    subagentId: string,
    provider: string,
    rows: readonly SubagentTranscriptRow[],
  ): void {
    const drawn = this.drawnFor(subagentId);
    const fresh = rows.filter((r) => r.seq > 0 && !drawn.has(r.seq)).sort((a, b) => a.seq - b.seq);
    if (fresh.length === 0) return;
    this.noteUpdate(subagentId);
    const newest = drawn.size > 0 ? Math.max(...drawn.keys()) : 0;
    for (const r of fresh) drawn.set(r.seq, r.item);
    if (fresh[0].seq > newest) {
      for (const r of fresh) {
        this.sink.handleEvent(subagentId, {
          type: "timeline",
          provider,
          item: r.item as AgentTimelineItem,
        });
      }
      return;
    }
    const ordered = [...drawn.entries()].sort((a, b) => a[0] - b[0]);
    this.sink.replace(
      subagentId,
      ordered.map(([seq, item]) => Object.assign({}, item as Record<string, unknown>, { seq })),
    );
    const desc = this.descriptorFor(parentAgentId, subagentId);
    const terminal = desc && terminalEventFor(desc);
    if (terminal) this.sink.handleEvent(subagentId, terminal);
  }

  private noteUpdate(subagentId: string): void {
    const next = new Map(this.lastUpdateAt);
    next.set(subagentId, Date.now());
    this.lastUpdateAt = next;
    this.armStallTimer();
  }

  private armStallTimer(): void {
    clearTimeout(this.stallTimer);
    this.stallTimer = undefined;
    const now = Date.now();
    let next = Number.POSITIVE_INFINITY;
    for (const desc of this.descriptors.values()) {
      const last = desc.status === "running" ? this.lastUpdateAt.get(desc.id) : undefined;
      if (last != null && last + SUBAGENT_STALL_CEILING_MS > now) {
        next = Math.min(next, last + SUBAGENT_STALL_CEILING_MS);
      }
    }
    if (next === Number.POSITIVE_INFINITY) return;
    this.stallTimer = setTimeout(() => {
      this.stallEpoch += 1;
      this.armStallTimer();
    }, next - now);
  }

  private dropUpdateClock(subagentId: string): void {
    if (!this.lastUpdateAt.has(subagentId)) return;
    const next = new Map(this.lastUpdateAt);
    next.delete(subagentId);
    this.lastUpdateAt = next;
  }

  lastUpdateFor(subagentId: string): number | null {
    return this.lastUpdateAt.get(subagentId) ?? null;
  }

  // Record what a fetched page said about rows BEHIND it. Called with the server's
  // `hasOlder`, including `false` — a later complete answer must be able to clear an
  // earlier cut one, so this sets AND unsets.
  noteTranscriptTruncated(subagentId: string, truncated: boolean): void {
    if (this.truncatedTranscripts.has(subagentId) === truncated) return;
    const next = new Set(this.truncatedTranscripts);
    if (truncated) next.add(subagentId);
    else next.delete(subagentId);
    this.truncatedTranscripts = next;
  }

  isTranscriptTruncated(subagentId: string): boolean {
    return this.truncatedTranscripts.has(subagentId);
  }

  // Whether a subagent has gone silent past the display ceiling. Only meaningful
  // for a "running" row — callers gate on status. A subagent we have never
  // received data for has no clock and is treated as not stalled (unknowable).
  isStalled(subagentId: string, now: number): boolean {
    const last = this.lastUpdateFor(subagentId);
    return last != null && now - last >= SUBAGENT_STALL_CEILING_MS;
  }

  private upsert(subagent: ProviderSubagentDescriptor): void {
    const key = providerSubagentKey(subagent.parentAgentId, subagent.id);
    const previous = this.descriptors.get(key);
    const next = new Map(this.descriptors);
    next.set(key, subagent);
    this.descriptors = next;
    this.noteUpdate(subagent.id);
    if (subagent.status === "running" && this.hiddenFromTrack.has(key)) {
      const hidden = new Set(this.hiddenFromTrack);
      hidden.delete(key);
      this.hiddenFromTrack = hidden;
    }
    // On the transition INTO a terminal status, emit the matching turn event so
    // an open live view settles (done / errored). Only fire on the edge.
    if (previous?.status !== subagent.status) {
      const terminal = terminalEventFor(subagent);
      if (terminal) this.sink.handleEvent(subagent.id, terminal);
    }
  }

  private remove(parentAgentId: string, subagentId: string): void {
    const key = providerSubagentKey(parentAgentId, subagentId);
    if (this.descriptors.has(key)) {
      const next = new Map(this.descriptors);
      next.delete(key);
      this.descriptors = next;
    }
    if (this.hiddenFromTrack.has(key)) {
      const hidden = new Set(this.hiddenFromTrack);
      hidden.delete(key);
      this.hiddenFromTrack = hidden;
    }
    this.dropUpdateClock(subagentId);
    this.noteTranscriptTruncated(subagentId, false);
    this.clearStream(subagentId);
  }

  // Replace the full descriptor set for one parent (the list.response snapshot).
  // Descriptors of OTHER parents are untouched.
  replaceList(parentAgentId: string, subagents: ProviderSubagentDescriptor[]): void {
    const replacementIds = new Set(subagents.map((subagent) => subagent.id));
    for (const desc of this.descriptors.values()) {
      if (
        desc.parentAgentId === parentAgentId &&
        !replacementIds.has(desc.id) &&
        !this.viewing.has(desc.id)
      ) {
        this.dropUpdateClock(desc.id);
        this.noteTranscriptTruncated(desc.id, false);
        this.clearStream(desc.id);
      }
    }
    const next = new Map<string, ProviderSubagentDescriptor>();
    for (const [key, desc] of this.descriptors) {
      if (
        desc.parentAgentId !== parentAgentId ||
        (this.viewing.has(desc.id) && !replacementIds.has(desc.id))
      ) {
        next.set(key, desc);
      }
    }
    const hidden = new Set(this.hiddenFromTrack);
    const clocks = new Map(this.lastUpdateAt);
    for (const subagent of subagents) {
      const key = providerSubagentKey(parentAgentId, subagent.id);
      // A snapshot is older than the live stream it raced: never let it downgrade a newer
      // status (a "completed" upsert overwritten by a late "running" list answer).
      const held = this.descriptors.get(key);
      const chosen =
        held && Date.parse(held.updatedAt) > Date.parse(subagent.updatedAt) ? held : subagent;
      next.set(key, chosen);
      if (chosen.status === "running") hidden.delete(key);
      // Word about the subagent, but no newer than its own stamp: a stuck row stays stalled.
      const stamped = Date.parse(chosen.updatedAt);
      clocks.set(
        subagent.id,
        Number.isFinite(stamped) ? Math.min(Date.now(), stamped) : Date.now(),
      );
    }
    this.descriptors = next;
    this.hiddenFromTrack = hidden;
    this.lastUpdateAt = clocks;
    this.armStallTimer();
  }

  // The visible (non-hidden) rows for one parent, oldest-first.
  rowsForParent(parentAgentId: string): ProviderSubagentRow[] {
    const rows: ProviderSubagentRow[] = [];
    for (const [key, desc] of this.descriptors) {
      if (desc.parentAgentId !== parentAgentId) continue;
      if (this.hiddenFromTrack.has(key)) continue;
      rows.push(toProviderSubagentRow(desc));
    }
    return sortProviderSubagentRows(rows);
  }

  descriptorFor(parentAgentId: string, subagentId: string): ProviderSubagentDescriptor | undefined {
    return this.descriptors.get(providerSubagentKey(parentAgentId, subagentId));
  }

  runningCountForParent(parentAgentId: string): number {
    return this.rowsForParent(parentAgentId).filter((r) => r.status === "running").length;
  }

  workingCountForParent(
    parentAgentId: string,
    parentTurnActive: boolean,
    now = Date.now(),
  ): number {
    void this.stallEpoch;
    return this.rowsForParent(parentAgentId).filter(
      (r) => r.status === "running" && (parentTurnActive || !this.isStalled(r.id, now)),
    ).length;
  }

  finishedCountForParent(parentAgentId: string): number {
    return this.rowsForParent(parentAgentId).filter((r) => isProviderSubagentFinished(r.status))
      .length;
  }

  // Archive one finished subagent off the track (local hide + drop its stream).
  hide(parentAgentId: string, subagentId: string): void {
    const key = providerSubagentKey(parentAgentId, subagentId);
    const hidden = new Set(this.hiddenFromTrack);
    hidden.add(key);
    this.hiddenFromTrack = hidden;
    this.dropUpdateClock(subagentId);
    this.noteTranscriptTruncated(subagentId, false);
    this.clearStream(subagentId);
  }

  // Archive every FINISHED subagent of a parent in one go (header action).
  hideFinishedForParent(parentAgentId: string): void {
    const hidden = new Set(this.hiddenFromTrack);
    const clocks = new Map(this.lastUpdateAt);
    for (const [key, desc] of this.descriptors) {
      if (desc.parentAgentId !== parentAgentId) continue;
      if (isProviderSubagentFinished(desc.status)) {
        hidden.add(key);
        clocks.delete(desc.id);
        this.clearStream(desc.id);
      }
    }
    this.hiddenFromTrack = hidden;
    this.lastUpdateAt = clocks;
  }

  // Drop everything (workspace switch / disconnect).
  clearAll(): void {
    clearTimeout(this.stallTimer);
    this.stallTimer = undefined;
    for (const [, desc] of this.descriptors) this.clearStream(desc.id);
    this.drawn.clear();
    this.descriptors = new Map();
    this.hiddenFromTrack = new Set();
    this.lastUpdateAt = new Map();
    this.truncatedTranscripts = new Set();
    this.activeWorkspaceId = null;
    this.workspaceByParent.clear();
    this.parentsByWorkspace.clear();
    this.workspaceRecency.clear();
  }
}
